import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import {
  CheckEmployeeRequest,
  CheckEmployeeRequestApiResponse,
  CheckEmployeeRequestResponse,
} from '@qaroni-app/check/types/check-employee-request';
import { CommonsHttpService } from '@qaroni-core/services/commons/commons-http/commons-http.service';
import { QaroniIntermediate } from '@qaroni-core/types/qaroni-intermediate/qaroni-intermediate';
import { Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import {
  RequestSummariesCount,
  RequestSummariesCountApiObject,
} from '../types/request-summaries-count';
import { ScheduleRequestsMultiple } from '../types/schedule-requests-multiple';
import { ScheduleRequestsHttpService } from './schedule-requests-http.service';
import { ScheduleSnackbarsService } from './schedule-snackbars.service';

@Injectable({
  providedIn: 'root',
})
export class ScheduleRequestsService extends QaroniIntermediate {
  private scheduleRequestsSubject = new Subject<CheckEmployeeRequest[]>();
  private scheduleRequestSubject = new Subject<CheckEmployeeRequest | null>();
  private scheduleRequestCounterSubject =
    new Subject<RequestSummariesCount | null>();

  private http = inject(ScheduleRequestsHttpService);
  private snackbarService = inject(ScheduleSnackbarsService);
  private commonHttp = inject(CommonsHttpService);

  public getScheduleRequestsSubject$() {
    return this.scheduleRequestsSubject.asObservable().pipe(shareReplay(1));
  }

  public getResponseRequestSubject$() {
    return this.scheduleRequestSubject.asObservable().pipe(shareReplay(1));
  }

  public getCounterSubject$() {
    return this.scheduleRequestCounterSubject
      .asObservable()
      .pipe(shareReplay(1));
  }

  public getAllRequests(companyID: number, params?: Params) {
    this.http.getAllRequests$(companyID, params).subscribe({
      next: this.nextCompanyRequests,
      error: this.errorCompanyRequests,
    });
  }

  private nextCompanyRequests = (response: CheckEmployeeRequestResponse) => {
    if (!response.result) {
      this.scheduleRequestsSubject.next([]);
    }
    this.scheduleRequestsSubject.next(response.result);
    this.linksSubject.next(response.links);
  };

  private errorCompanyRequests = () => {
    this.scheduleRequestsSubject.next([]);
  };

  public approveRequest(
    companyID: number,
    employeeID: number,
    requestID: number
  ) {
    this.http.approveRequest$(companyID, employeeID, requestID).subscribe({
      next: this.getApproveRequest,
      error: this.errorApproveRequest,
    });
  }

  private getApproveRequest = (response: CheckEmployeeRequestResponse) => {
    if (!response) {
      this.snackbarService.failChangeRequestStatus();
      this.scheduleRequestSubject.next(null);
      return;
    }
    this.snackbarService.successChangeRequestStatus();
    this.scheduleRequestSubject.next(response.result[0]);
  };

  private errorApproveRequest = (error: HttpErrorResponse) => {
    if (this.commonHttp.errorsHttp.isControlledError(error, 'E0028')) {
      this.snackbarService.failRequestCollisionDates();
      this.scheduleRequestSubject.next(null);
      return;
    }

    this.snackbarService.failChangeRequestStatus();
    this.scheduleRequestSubject.next(null);
  };

  public rejectRequest(
    companyID: number,
    employeeID: number,
    requestID: number
  ) {
    this.http.rejectRequest$(companyID, employeeID, requestID).subscribe({
      next: this.getRejectRequest,
      error: this.errorRejectRequest,
    });
  }

  private getRejectRequest = (response: CheckEmployeeRequestResponse) => {
    if (!response) {
      this.snackbarService.failChangeRequestStatus();
      this.scheduleRequestSubject.next(null);
      return;
    }

    this.snackbarService.successChangeRequestStatus();
    this.scheduleRequestSubject.next(response.result[0]);
  };

  private errorRejectRequest = () => {
    this.snackbarService.failChangeRequestStatus();
    this.scheduleRequestSubject.next(null);
  };

  public counter(companyID: number, params?: Params) {
    this.http.counter$(companyID, params).subscribe({
      next: this.nextCounter,
      error: this.errorCounter,
    });
  }

  private nextCounter = (response: RequestSummariesCountApiObject | null) => {
    if (!response) {
      this.scheduleRequestCounterSubject.next(null);
      return;
    }

    this.scheduleRequestCounterSubject.next(response.result[0]);
  };

  private errorCounter = () => {
    this.scheduleRequestCounterSubject.next(null);
  };

  public approveMultipleRequests(
    companyID: number,
    request: ScheduleRequestsMultiple
  ) {
    this.http.approveMultipleRequests$(companyID, request).subscribe({
      next: this.getApproveMultipleRequests,
      error: this.errorApproveMultipleRequests,
    });
  }

  private getApproveMultipleRequests = (
    response: CheckEmployeeRequestApiResponse
  ) => {
    if (this.commonHttp.validationsHttp.verifyStatus204(response)) {
      this.snackbarService.successChangeRequestStatus();
      this.messageSubject.next('approved Ok');
      return;
    }

    this.snackbarService.failChangeRequestStatus();
    this.messageSubject.next('');
  };

  private errorApproveMultipleRequests = () => {
    this.snackbarService.failChangeRequestStatus();
    this.messageSubject.next('');
  };

  public rejectMultipleRequests(
    companyID: number,
    request: ScheduleRequestsMultiple
  ) {
    this.http.rejectMultipleRequests$(companyID, request).subscribe({
      next: this.getRejectMultipleRequests,
      error: this.errorRejectMultipleRequests,
    });
  }

  private getRejectMultipleRequests = (
    response: CheckEmployeeRequestApiResponse
  ) => {
    if (this.commonHttp.validationsHttp.verifyStatus204(response)) {
      this.snackbarService.successChangeRequestStatus();
      this.messageSubject.next('rejected Ok');
      return;
    }

    this.snackbarService.failChangeRequestStatus();
    this.messageSubject.next('');
  };

  private errorRejectMultipleRequests = () => {
    this.snackbarService.failChangeRequestStatus();
    this.messageSubject.next('');
  };
}
